import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Mail, MailOpen, Loader2 } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar, Legend } from "recharts";
import { api, errMsg } from "@/lib/api";

const PLANS = [["trial", "7-day trial"], ["1m", "1 month"], ["3m", "3 months"], ["6m", "6 months"], ["1y", "1 year"]];
const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const daysLeft = (d) => Math.ceil((new Date(d) - Date.now()) / 86400000);

export const LicenseBadge = ({ u }) => {
  if (u.is_admin) return <span className="rounded-full bg-[#3D3DF0] px-2.5 py-0.5 text-[10px] font-bold text-white">ADMIN</span>;
  if (!u.license) return <span className="rounded-full bg-slate-300 px-2.5 py-0.5 text-[10px] font-bold">NONE</span>;
  const left = daysLeft(u.license.expires_at);
  return <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold text-white ${!u.license_active ? "bg-[#A62B8A]" : u.license.plan === "trial" ? "bg-[#F7933E]" : "bg-[#5FA82E]"}`}>{u.license_active ? `${PLANS.find((p) => p[0] === u.license.plan)?.[1] || u.license.plan} · ${left}d left` : "EXPIRED"}</span>;
};

export const UsersTab = () => {
  const [users, setUsers] = useState(null);
  const load = useCallback(() => api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const assign = async (u, plan) => { await api.put(`/admin/users/${u.user_id}/license`, { plan }); toast.success(`${u.email}: ${PLANS.find((p) => p[0] === plan)[1]} license assigned`); load(); };
  const remove = async (u) => { if (!window.confirm(`Delete ${u.email} and all their books?`)) return; try { await api.delete(`/admin/users/${u.user_id}`); toast.success("User deleted"); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!users) return <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#3D3DF0]" />;
  return (
    <div className="space-y-3" data-testid="admin-users">
      {users.map((u) => (
        <div key={u.user_id} className="neu-raised flex flex-wrap items-center gap-4 rounded-3xl p-5" data-testid={`admin-user-${u.user_id}`}>
          <div className="min-w-[220px] flex-1">
            <div className="flex items-center gap-2"><p className="font-display text-lg font-bold">{u.name || "—"}</p><LicenseBadge u={u} /></div>
            <p className="text-sm text-[#64748B]">{u.email}</p>
            <p className="mt-1 font-mono text-[11px] text-[#64748B]">joined {fmt(u.created_at)} · last login {fmt(u.last_login)} · {u.books} books · {u.published} published · {u.views} views · {u.usage.upload || 0} uploads · {u.usage.ai_analyze || 0} AI runs</p>
          </div>
          {!u.is_admin && (
            <div className="flex flex-wrap items-center gap-2">
              {PLANS.slice(1).map(([p, l]) => <button key={p} onClick={() => assign(u, p)} data-testid={`admin-license-${p}-${u.user_id}`} className="neu-btn rounded-full px-3 py-1.5 text-xs font-semibold">{l}</button>)}
              <button onClick={() => remove(u)} aria-label="Delete user" data-testid={`admin-delete-${u.user_id}`} className="neu-btn rounded-full p-2 text-[#A62B8A]"><Trash2 className="h-4 w-4" /></button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export const MessagesTab = () => {
  const [msgs, setMsgs] = useState(null);
  const load = useCallback(() => api.get("/admin/messages").then((r) => setMsgs(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const read = async (m) => { await api.patch(`/admin/messages/${m.id}`); load(); };
  const del = async (m) => { await api.delete(`/admin/messages/${m.id}`); toast.success("Message deleted"); load(); };
  if (!msgs) return <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#3D3DF0]" />;
  if (!msgs.length) return <p className="py-10 text-center text-sm text-[#64748B]" data-testid="admin-messages-empty">No messages yet.</p>;
  return (
    <div className="space-y-3" data-testid="admin-messages">
      {msgs.map((m) => (
        <div key={m.id} className={`rounded-3xl p-5 ${m.read ? "neu-flat" : "neu-raised ring-2 ring-[#3D3DF0]/30"}`} data-testid={`admin-msg-${m.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-display text-base font-bold">{m.subject || "(no subject)"} <span className="ml-2 rounded-full bg-[#A62B8A]/15 px-2 py-0.5 text-[10px] font-bold uppercase text-[#A62B8A]">{m.kind}</span></p>
              <p className="text-xs text-[#64748B]">{m.name} · {m.email} · {new Date(m.created_at).toLocaleString()}{m.user_id ? " · registered user" : ""}</p>
            </div>
            <div className="flex gap-2">
              {!m.read && <button onClick={() => read(m)} aria-label="Mark read" data-testid={`admin-msg-read-${m.id}`} className="neu-btn rounded-full p-2"><MailOpen className="h-4 w-4" /></button>}
              <a href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject || "iBookStudio")}`} aria-label="Reply" className="neu-btn rounded-full p-2"><Mail className="h-4 w-4" /></a>
              <button onClick={() => del(m)} aria-label="Delete message" data-testid={`admin-msg-delete-${m.id}`} className="neu-btn rounded-full p-2 text-[#A62B8A]"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
          <p className="mt-3 whitespace-pre-wrap text-sm">{m.message}</p>
        </div>
      ))}
    </div>
  );
};

const COLORS = ["#3D3DF0", "#A62B8A", "#A62B8A", "#F7933E", "#26C4D5", "#5FA82E"];

export const AnalyticsTab = () => {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setA(r.data)); }, []);
  if (!a) return <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#3D3DF0]" />;
  const t = a.totals;
  const cards = [["Users", t.users], ["Active licenses", t.active_licenses], ["Books", t.books], ["Published", t.published], ["Book views", t.views], ["Page views", t.page_views], ["Taps", t.taps], ["Quiz attempts", t.quiz_attempts], ["Messages", `${t.messages} (${t.unread} new)`]];
  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {cards.map(([l, v], i) => (
          <div key={l} className="neu-raised rounded-3xl p-5" data-testid={`admin-stat-${l.toLowerCase().replace(/ /g, "-")}`}>
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#64748B]">{l}</p>
            <p className="mt-1 font-display text-3xl font-extrabold" style={{ color: COLORS[i % COLORS.length] }}>{v}</p>
          </div>
        ))}
      </div>
      <div className="neu-raised rounded-3xl p-5">
        <p className="mb-3 font-display font-bold">Last 30 days · signups & reading</p>
        <div className="h-60 min-w-0"><ResponsiveContainer width="100%" height={240}><AreaChart data={a.daily}><XAxis dataKey="day" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} allowDecimals={false} /><Tooltip /><Legend />
          <Area type="monotone" dataKey="page_views" stroke="#3D3DF0" fill="#3D3DF0" fillOpacity={0.2} name="Page views" />
          <Area type="monotone" dataKey="signups" stroke="#A62B8A" fill="#A62B8A" fillOpacity={0.2} name="Signups" /></AreaChart></ResponsiveContainer></div>
      </div>
      <div className="neu-raised rounded-3xl p-5">
        <p className="mb-3 font-display font-bold">Author usage (logins, uploads, AI, publishes)</p>
        <div className="h-60 min-w-0"><ResponsiveContainer width="100%" height={240}><BarChart data={a.daily}><XAxis dataKey="day" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} allowDecimals={false} /><Tooltip /><Legend />
          {a.actions.map((k, i) => <Bar key={k} dataKey={k} stackId="u" fill={COLORS[i % COLORS.length]} />)}</BarChart></ResponsiveContainer></div>
      </div>
      <div className="grid gap-6 md:grid-cols-2">
        <div className="neu-raised rounded-3xl p-5"><p className="mb-2 font-display font-bold">Licenses</p>{Object.entries(a.plans).map(([k, v]) => <div key={k} className="flex justify-between py-1 text-sm"><span className="uppercase">{k}</span><b>{v}</b></div>)}</div>
        <div className="neu-raised rounded-3xl p-5"><p className="mb-2 font-display font-bold">Top books</p>{a.top_books.map((b) => <div key={b.slug} className="flex justify-between py-1 text-sm"><span className="truncate">{b.title}</span><b>{b.views || 0}</b></div>)}</div>
      </div>
    </div>
  );
};

export const SettingsTab = () => {
  const [ga, setGa] = useState("");
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => { setGa(r.data.ga_measurement_id); setLoaded(true); }); }, []);
  const save = async () => { await api.put("/admin/settings", { ga_measurement_id: ga }); toast.success(ga ? "Google Analytics enabled" : "Google Analytics disabled"); };
  return (
    <div className="neu-raised max-w-xl space-y-4 rounded-3xl p-6" data-testid="admin-settings">
      <p className="font-display text-lg font-bold">Google Analytics 4</p>
      <p className="text-sm text-[#64748B]">Paste your GA4 Measurement ID (looks like G-XXXXXXX). The tracking tag is added to every page, including published readers. View full GA reports at analytics.google.com.</p>
      <input value={ga} onChange={(e) => setGa(e.target.value)} placeholder="G-XXXXXXXXXX" className="neu-inset w-full rounded-xl px-4 py-2.5 font-mono text-sm focus:outline-none" data-testid="admin-ga-input" />
      <button onClick={save} disabled={!loaded} className="neu-btn-primary rounded-full px-6 py-2.5 text-sm font-bold disabled:opacity-50" data-testid="admin-ga-save">Save</button>
    </div>
  );
};
